import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, PhoneCall, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'web',
    phone: '',
    message: ''
  });
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.', {
        style: {
          borderRadius: '10px',
          background: '#002a5c',
          color: '#fff',
        },
      });
      return;
    }

    setIsSending(true);
    const loadingToast = toast.loading('Sending your message...', {
      style: {
        borderRadius: '10px',
        background: '#002a5c',
        color: '#fff',
      },
    });

    const submissionData = new FormData();
    submissionData.append("access_key", "9fcd84f7-aceb-44de-9438-ab9a40a73fb7");
    submissionData.append("name", formData.name);
    submissionData.append("email", formData.email);
    submissionData.append("phone", formData.phone);
    submissionData.append("subject", `Contact Enquiry: ${formData.subject}`);
    submissionData.append("message", formData.message);
    submissionData.append("from_name", "OETechs Contact Form");
    // Web3Forms will send to the email associated with this access key
    // Usually info@oetechs.com or venkat@oetechs.com as configured in their dashboard

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: submissionData
      });

      const data = await response.json();

      if (data.success) {
        toast.dismiss(loadingToast);
        toast.success('Message sent successfully! We will get back to you soon.', {
          icon: '🚀',
          style: {
            borderRadius: '10px',
            background: '#002a5c',
            color: '#fff',
          },
        });
        setFormData({
          name: '',
          email: '',
          subject: 'web',
          phone: '',
          message: ''
        });
      } else {
        throw new Error(data.message || "Failed to send message");
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      toast.error(error.message || 'Failed to send message. Please try again later.', {
        style: {
          borderRadius: '10px',
          background: '#002a5c',
          color: '#fff',
        },
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] min-h-screen transition-colors duration-500">
      <section className="bg-white dark:bg-[#001a35] border-b border-slate-100 dark:border-white/5 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-blue-600 font-semibold uppercase tracking-widest text-sm mb-4 block">Get In Touch</span>
            <h1 className="text-4xl md:text-5xl font-semibold text-[#002a5c] dark:text-white mb-6">Let's Build Something Great Together</h1>
            <p className="text-slate-500 dark:text-slate-400 text-lg max-w-3xl mx-auto leading-relaxed">
              Have a question or a project in mind? Our team is here to help you navigate your digital journey.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div className="bg-white dark:bg-[#001a35] p-8 rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-white/10">
                <h3 className="text-2xl font-semibold text-[#002a5c] dark:text-white mb-8">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center shrink-0">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#002a5c] dark:text-white text-sm uppercase tracking-wider mb-1">Our Office</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                        Gachibowli, Hyderabad, Telangana - 500032
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center shrink-0">
                      <PhoneCall size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#002a5c] dark:text-white text-sm uppercase tracking-wider mb-1">Phone</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">
                        +91 9849 231 222
                      </p>
                   
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center shrink-0">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#002a5c] dark:text-white text-sm uppercase tracking-wider mb-1">Email</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">
                        info@oetechs.com
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-2xl flex items-center justify-center shrink-0">
                      <Clock size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#002a5c] dark:text-white text-sm uppercase tracking-wider mb-1">Business Hours</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">
                        Mon - Sat: 9:00 AM - 7:00 PM
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-12 border-t border-slate-100 dark:border-white/5">
                  <h4 className="font-semibold text-[#002a5c] dark:text-white text-sm uppercase tracking-wider mb-6 text-center">Join Our Community</h4>
                  <a 
                    href="https://whatsapp.com/channel/0029VbDBi0XFcovz5xuPCe1B"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 w-full py-4 bg-[#25D366] text-white rounded-2xl font-semibold hover:scale-[1.02] transition-transform shadow-lg shadow-green-500/20"
                  >
                    <MessageCircle size={20} />
                    Join on WhatsApp
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-[#001a35] p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-white/10">
                <h3 className="text-2xl font-semibold text-[#002a5c] dark:text-white mb-8">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest ml-1">Full Name</label>
                      <input 
                        required
                        type="text" 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full bg-slate-50 dark:bg-white/5 border border-transparent focus:border-blue-500 dark:focus:border-blue-500 rounded-2xl py-4 px-6 text-slate-900 dark:text-white outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                      <input 
                        required
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="w-full bg-slate-50 dark:bg-white/5 border border-transparent focus:border-blue-500 dark:focus:border-blue-500 rounded-2xl py-4 px-6 text-slate-900 dark:text-white outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest ml-1">Subject</label>
                      <select 
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full bg-slate-50 dark:bg-white/5 border border-transparent focus:border-blue-500 dark:focus:border-blue-500 rounded-2xl py-4 px-6 text-slate-900 dark:text-white outline-none transition-all appearance-none"
                      >
                        <option value="web">Web Development</option>
                        <option value="mobile">Mobile App</option>
                        <option value="marketing">Digital Marketing</option>
                        <option value="other">Other Enquiry</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest ml-1">Phone Number</label>
                      <input 
                        required
                        type="tel" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 00000 00000"
                        className="w-full bg-slate-50 dark:bg-white/5 border border-transparent focus:border-blue-500 dark:focus:border-blue-500 rounded-2xl py-4 px-6 text-slate-900 dark:text-white outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest ml-1">Your Message</label>
                    <textarea 
                      required
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      rows={6}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-transparent focus:border-blue-500 dark:focus:border-blue-500 rounded-2xl py-4 px-6 text-slate-900 dark:text-white outline-none transition-all resize-none"
                    ></textarea>
                  </div>

                  <button 
                    type="submit"
                    disabled={isSending}
                    className="w-full py-4 bg-blue-600 text-white rounded-2xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSending ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    )}
                    {isSending ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
